import { Check, TriangleAlert } from 'lucide-react';
import { Fragment, useMemo } from 'react';
import {
  COTES_MONTAGE,
  MATIERES_MONTURE,
  MATIERE_MONTURE_LABELS,
  parseAddition,
  parseOeil,
  type CoteMontage,
  type MatiereMonture,
} from '@sirajoptic/shared';
import { Field, Input, InputField } from '@/components/ui/field';
import { cn } from '@/lib/utils';

/**
 * Saisie de l'ordonnance dans **sa** notation : `180'-0,25/-0,75`. Le champ
 * accepte le texte tel qu'il l'écrit et affiche en dessous l'interprétation
 * AXE / SPH / CYL, pour qu'il voie immédiatement si l'application a compris.
 *
 * Aucune valeur d'exemple dans le champ : grisée, elle se confondait avec une
 * vraie correction et gênait la relecture. Le rappel de la notation vit sous
 * le champ, tant qu'il est vide.
 *
 * Une saisie non reconnue n'est jamais bloquée : elle est simplement signalée,
 * et la commande partira marquée « à vérifier » (règle 14).
 */
export function OeilInput({
  label,
  valeur,
  onChange,
}: {
  label: string;
  valeur: string;
  onChange: (v: string) => void;
}) {
  const analyse = useMemo(() => parseOeil(valeur), [valeur]);
  const vide = !valeur.trim();

  return (
    <Field label={label}>
      <Input
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
        className={cn('font-mono', !vide && !analyse.ok && 'champ-erreur')}
        aria-describedby={`${label}-analyse`}
      />
      <p
        id={`${label}-analyse`}
        className={cn(
          'mt-1 flex items-center gap-1.5 text-xs',
          vide ? 'text-ink-muted' : analyse.ok ? 'text-ink-muted' : 'text-danger',
        )}
      >
        {vide ? (
          <span className="text-ink-muted">AXE&apos;SPH/CYL — ou « PL » pour plan</span>
        ) : analyse.ok ? (
          <>
            <Check aria-hidden className="h-3 w-3 text-success" />
            <span className="tabular">
              {analyse.axe ? `AXE ${analyse.axe} · ` : ''}
              {analyse.sph ? `SPH ${analyse.sph}` : 'SPH —'}
              {analyse.cyl ? ` · CYL ${analyse.cyl}` : ''}
            </span>
          </>
        ) : (
          <>
            <TriangleAlert aria-hidden className="h-3 w-3" />
            <span>Notation non reconnue — la saisie est conservée, à vérifier plus tard.</span>
          </>
        )}
      </p>
    </Field>
  );
}

export function AdditionInput({
  valeur,
  onChange,
}: {
  valeur: string;
  onChange: (v: string) => void;
}) {
  const analyse = useMemo(() => parseAddition(valeur), [valeur]);
  const vide = !valeur.trim();

  return (
    <Field label="ADD (addition)">
      <Input
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
        className={cn('font-mono', !vide && !analyse.ok && 'champ-erreur')}
      />
      <p className={cn('mt-1 text-xs', !vide && !analyse.ok ? 'text-danger' : 'text-ink-muted')}>
        {vide
          ? 'Vision de près'
          : analyse.ok
            ? `Addition ${analyse.addition}`
            : 'Valeur non reconnue — conservée telle quelle.'}
      </p>
    </Field>
  );
}

/** Champs de l'ordonnance pilotés par le formulaire de commande. */
export type ChampOrdonnance =
  | 'od_raw'
  | 'og_raw'
  | 'addition'
  | 'prescripteur'
  | 'date_ordonnance';

/** Bloc complet OD / OG / ADD, plus la provenance de la prescription. */
export function OrdonnanceBloc({
  od,
  og,
  addition,
  prescripteur,
  dateOrdonnance,
  onChange,
}: {
  od: string;
  og: string;
  addition: string;
  prescripteur: string;
  dateOrdonnance: string;
  onChange: (champ: ChampOrdonnance, valeur: string) => void;
}) {
  return (
    <div className="rounded-md border border-line bg-surface-sunken p-3">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
        Ordonnance
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <OeilInput label="OD (œil droit)" valeur={od} onChange={(v) => onChange('od_raw', v)} />
        <OeilInput label="OG (œil gauche)" valeur={og} onChange={(v) => onChange('og_raw', v)} />
        <AdditionInput valeur={addition} onChange={(v) => onChange('addition', v)} />
      </div>

      {/* D'où vient la prescription, et de quand elle date. */}
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <InputField
          label="Prescripteur"
          value={prescripteur}
          onChange={(e) => onChange('prescripteur', e.target.value)}
        />
        {/* L'indication AMO tient dans le libellé plutôt que dans une ligne
            d'aide dessous : même information, une ligne de moins — et c'est
            cette ligne qui faisait défiler le formulaire sur un portable. */}
        <InputField
          label="Date de l'ordonnance — départ du délai AMO"
          type="date"
          value={dateOrdonnance}
          onChange={(e) => onChange('date_ordonnance', e.target.value)}
        />
      </div>
    </div>
  );
}

/** Champs des mesures de montage : « <cote>_od » ou « <cote>_og ». */
export type ChampMesure = `${CoteMontage}_od` | `${CoteMontage}_og`;

const AIDES: Record<CoteMontage, string> = {
  ecart: 'Du centre du nez au centre de la pupille.',
  hauteur: 'Du bas du calibre au centre de la pupille.',
  progression: 'Longueur du couloir : 14 pour un couloir court, 18 en standard.',
  largeur: 'Largeur de boîte du calibre.',
  diagonale: 'La plus grande : c’est elle qui décide du diamètre du palet.',
};

/**
 * Cotes de montage — ce que le laboratoire réclame en plus de la correction.
 *
 * Présentées comme sur un bon de laboratoire : **une ligne par œil, une
 * colonne par cote**. Empilées une cote par ligne, avec leur explication
 * dessous, elles prenaient à elles seules la moitié de la hauteur du
 * formulaire, et c'est ce bloc qui obligeait à défiler pour atteindre
 * « Créer la commande ».
 *
 * **L'ordre de tabulation, lui, n'a pas changé.** La grille se remplit par
 * colonnes (`grid-flow-col`) : dans le document, l'écart OD est suivi de
 * l'écart OG, puis de la hauteur OD — exactement l'ordre dans lequel les cotes
 * se relèvent et se dictent. Seul l'affichage est transposé ; les doigts sur
 * le clavier font le même chemin qu'avant.
 *
 * L'explication de chaque cote passe dans l'infobulle de son en-tête. Elle
 * sert la première semaine ; ensuite elle prenait une ligne à chaque cote.
 *
 * Une valeur par œil pour toutes, y compris la largeur et la diagonale : le
 * décentrement diffère d'un côté à l'autre, donc le palet utile aussi.
 *
 * Saisie libre, comme l'ordonnance. Ni contrôle de plage ni conversion : une
 * borne « raisonnable » écrite ici refuserait un jour l'écart d'un enfant, et
 * l'opticien sait avant nous si sa valeur est bonne.
 */
export function MesuresBloc({
  valeurs,
  matiere,
  onChangeMatiere,
  onChange,
}: {
  valeurs: Record<ChampMesure, string>;
  /** Matière de la monture, `null` tant qu'elle n'a pas été précisée. */
  matiere: MatiereMonture | null;
  onChangeMatiere: (matiere: MatiereMonture | null) => void;
  onChange: (champ: ChampMesure, valeur: string) => void;
}) {
  return (
    <div className="rounded-md border border-line bg-surface-sunken p-3">
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
          Cotes de montage
        </p>
        <p className="text-xs text-ink-muted">
          En millimètres, œil par œil. Indispensables pour commander un progressif.
        </p>
      </div>

      {/* Défilement horizontal en dernier recours seulement : sur un téléphone,
          cinq colonnes de saisie ne tiennent pas, et mieux vaut glisser que
          d'écraser les champs. */}
      <div className="overflow-x-auto">
        <div
          className="grid min-w-[26rem] grid-flow-col grid-cols-[2rem_repeat(5,minmax(0,1fr))]
            grid-rows-[auto_auto_auto] items-center gap-x-2 gap-y-1"
        >
          {/* Première colonne : les yeux. La case d'angle reste vide. */}
          <span aria-hidden />
          <span aria-hidden className="text-xs font-semibold text-ink-muted">
            OD
          </span>
          <span aria-hidden className="text-xs font-semibold text-ink-muted">
            OG
          </span>

          {COTES_MONTAGE.map((cote) => (
            <Fragment key={cote.cle}>
              <span
                aria-hidden
                title={`${cote.libelle} — ${AIDES[cote.cle]}`}
                className="cursor-help text-center text-xs font-medium leading-tight text-ink-soft"
              >
                {cote.libelle}
              </span>
              {(['od', 'og'] as const).map((oeil) => {
                const champ = `${cote.cle}_${oeil}` as ChampMesure;
                return (
                  <Input
                    key={oeil}
                    inputMode="decimal"
                    className="text-center font-mono"
                    value={valeurs[champ]}
                    onChange={(e) => onChange(champ, e.target.value)}
                    aria-label={`${cote.libelle} — ${oeil === 'od' ? 'œil droit' : 'œil gauche'}`}
                    title={AIDES[cote.cle]}
                  />
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>

      {/* La matière se choisit d'un coup d'œil, pas dans une liste déroulante :
          deux valeurs, et la réponse est déjà dans la main qui tient la
          monture. Un troisième bouton rend le champ effaçable — sans lui, une
          matière cochée par erreur ne pourrait plus être retirée, et une
          commande de verres seuls porterait une monture qu'elle n'a pas. */}
      <fieldset className="mt-3 border-t border-line pt-3">
        <legend className="sr-only">Matière de la monture</legend>
        <div className="flex flex-wrap items-center gap-2">
          <p
            className="mr-auto cursor-help text-sm font-medium text-ink"
            title="Le laboratoire ne biseaute pas un acétate comme un métal cerclé."
          >
            Matière de la monture
          </p>
          {MATIERES_MONTURE.map((m) => {
            const actif = matiere === m;
            return (
              <button
                key={m}
                type="button"
                aria-pressed={actif}
                onClick={() => onChangeMatiere(actif ? null : m)}
                className={cn(
                  'rounded-md border px-3 py-1 text-sm transition-colors',
                  actif
                    ? 'border-mustard-500 bg-mustard-100 font-medium text-ink'
                    : 'border-line bg-white text-ink-soft hover:border-ink-muted',
                )}
              >
                {MATIERE_MONTURE_LABELS[m]}
              </button>
            );
          })}
          <button
            type="button"
            aria-pressed={matiere === null}
            onClick={() => onChangeMatiere(null)}
            className={cn(
              'rounded-md border px-3 py-1 text-sm transition-colors',
              matiere === null
                ? 'border-line bg-surface-sunken font-medium text-ink-soft'
                : 'border-line bg-white text-ink-muted hover:border-ink-muted',
            )}
          >
            Non précisée
          </button>
        </div>
      </fieldset>
    </div>
  );
}
